import { useCallback, useState, useEffect } from 'react';
import "./m2mDetails.scss";
import service from "../../services/Service";
import { notification } from 'antd';
import Loading from "../../utils/loading";
import { withLocale } from '../../l10n';

/**
 * Datos de la tarjeta M2M de una instalación, solo para consulta.
 * Se muestra en el detalle de la instalación (Instalaciones > Detalles).
 */
function M2MDetails({ lang, identUUID }) {
  // ===== 1. ESTADOS =====
  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState({});
  const [icc, setIcc] = useState('');

  // ===== 3. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    if (!identUUID) return;

    setLoading(true);
    try {
      const boardRes = await service.get(`boards/uuid/${identUUID.id_spare}`);
      if (boardRes.data.ok === false) {
        notification["error"]({
          message: lang.handle(boardRes.data.message),
        });
        return;
      }

      const currentIcc = boardRes.data.icc;
      setIcc(currentIcc);

      const m2mRes = await service.get(`m2m/${currentIcc}`);
      if (m2mRes.data.ok === false) {
        notification["error"]({
          message: lang.handle(m2mRes.data.message),
        });
      } else {
        setDatos(m2mRes.data);
      }
    } catch (err) {
      console.error('Error fetching M2M details:', err);
      notification["error"]({
        message: lang.handle('Error al obtener datos'),
      });
    } finally {
      setLoading(false);
    }
  }, [identUUID, lang]);

  // ===== 4. HELPERS =====
  const RangoConsumo = (bytes) => {
    if (!Number.isFinite(bytes)) return "-";

    const sizes = ['bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 bytes';

    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
  };

  const TecnologiaConexion = (data) => {
    const techmap = {
      1: "3G",
      2: "2G",
      5: "3.5G",
      6: "4G",
      8: "NB-IoT"
    }
    return techmap[data] ?? lang.handle('error')
  };

  // ===== 5. EFFECTS =====
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  // ===== 6. RENDER =====
  return (
    <div className="card">
      {loading && <Loading loading={loading} />}
      {/* Titulo de la tarjeta */}
      <h4 className="card-title p-3"> {lang.handle('Información de tarjeta M2M con icc') + ': ' + icc} </h4>

      {/* Datos de la tarjeta, solo lectura */}
      <div className='p-3'>

        {/* alias tarjeta */}
        <div className="form-group row">
          <label htmlFor="alias" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Alias')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="alias"
              value={datos.alias ?? ""}
              disabled={true}
            />
          </div>
        </div>
        {/* lifeCycle status */}
        <div className="form-group row">
          <label htmlFor="lifecycle" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Life Cycle Status')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="lifecycle"
              value={datos.lifeCycleStatus ?? ""}
              disabled={true}
            />
          </div>
        </div>
        {/* cliente */}
        <div className="form-group row">
          <label htmlFor="cliente" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Cliente')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="cliente"
              value={datos.customField1 ?? ""}
              disabled={true}
            />
          </div>
        </div>
        {/* Consumos */}
        <div className="form-group row">
          {/* Diarios */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Consumo diario')} </div>
          <div className="col-sm-4">
            <input
              className="form-control"
              value={RangoConsumo(datos.consumptionDaily?.data?.value)}
              disabled={true}
            />
          </div>

          {/* Mensuales */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Consumo mensual')} </div>
          <div className="col-sm-4">
            <input
              className="form-control"
              value={RangoConsumo(datos.consumptionMonthly?.data?.value)}
              disabled={true}
            />
          </div>
        </div>
        {/* Operador-Pais */}
        <div className="form-group row">
          {/* Operador */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Operador')} </div>
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              value={datos.operator ?? ""}
              disabled={true}
            />
          </div>

          {/* Pais */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Pais')} </div>
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              value={datos.country ?? ""}
              disabled={true}
            />
          </div>
        </div>
        {/* PLAN */}
        <div className="form-group row">
          {/* PLAN */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Plan')} </div>
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              value={datos.servicePack ?? ""}
              disabled={true}
            />
          </div>

          {/* POOL */}
          <div className="col-sm-2 newCard-form-text"> Pool </div>
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              value={datos.commercialGroup ?? ""}
              disabled={true}
            />
          </div>
        </div>
        {/* ESTADO GPRS */}
        <div className="form-group row">
          {/* Estado */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Estado Actual')} </div>
          <div className="col-sm-2">
            <input
              type="text"
              className="form-control"
              value={datos.presence?.level ?? ""}
              disabled={true}
            />
          </div>
          {/* GPRS */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Último evento')} </div>
          <div className="col-sm-2">
            <input
              type="text"
              className="form-control"
              value={datos.presence?.cause ?? ""}
              disabled={true}
            />
          </div>
          {/* Tipo Conexión */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Tecno.Conexión')} </div>
          <div className="col-sm-2">
            <input
              type="text"
              className="form-control"
              value={TecnologiaConexion(datos.presence?.ratType ?? "")}
              disabled={true}
            />
          </div>
        </div>
        <div className="form-group row">
          {/* Time */}
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Última Conexión')} </div>
          <div className="col-sm-4">
            <input
              type="text"
              className="form-control"
              value={datos.presence?.timestamp ?? ""}
              disabled={true}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default withLocale(M2MDetails);
